import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from "./ui/button";
import { MessageCircle, Phone } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MessagingButtons = ({ phoneNumber, clientName = "", ticketNumber = "" }) => {
  const [whatsappConfig, setWhatsappConfig] = useState(null);
  const [viberConfig, setViberConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchConfigs();
  }, []);

  const fetchConfigs = async () => {
    try {
      const [whatsappResponse, viberResponse] = await Promise.all([
        axios.get(`${API}/messaging/whatsapp/config`),
        axios.get(`${API}/messaging/viber/config`)
      ]);
      
      setWhatsappConfig(whatsappResponse.data);
      setViberConfig(viberResponse.data);
    } catch (error) {
      console.error("Fetch messaging configs error:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatPhoneNumber = (phone) => {
    // Remove all non-digit characters
    let cleaned = phone.replace(/\D/g, '');
    
    // If it starts with 0, replace with country code (Kosovo +383)
    if (cleaned.startsWith('0')) {
      cleaned = '383' + cleaned.substring(1);
    }
    
    // If it starts with 377, 378, or 379 (Albania), keep it
    if (cleaned.startsWith('355')) {
      return cleaned;
    }
    
    // If it doesn't start with country code, add Kosovo's
    if (!cleaned.startsWith('383') && !cleaned.startsWith('355') && !cleaned.startsWith('+')) {
      cleaned = '383' + cleaned;
    }
    
    return cleaned;
  };

  const createMessage = (template, platform) => {
    let message = template;
    
    // Replace placeholders
    if (clientName) {
      message = message.replace('{client_name}', clientName);
      message = message.replace('{emri}', clientName);
    }
    
    if (ticketNumber) {
      message = message.replace('{ticket_number}', ticketNumber);
      message = message.replace('{tiketi}', ticketNumber);
    }
    
    return message;
  };

  const openWhatsApp = () => {
    if (!whatsappConfig || !whatsappConfig.is_enabled) {
      toast.error("WhatsApp integrim nuk është i aktivizuar");
      return;
    }

    const formattedPhone = formatPhoneNumber(phoneNumber);
    const message = createMessage(whatsappConfig.default_message, 'whatsapp');
    
    // WhatsApp Web URL
    const whatsappUrl = `https://wa.me/${formattedPhone}?text=${encodeURIComponent(message)}`;
    
    // Open in new tab
    window.open(whatsappUrl, '_blank');
    
    toast.success("WhatsApp u hap me sukses");
  };

  const openViber = () => {
    if (!viberConfig || !viberConfig.is_enabled) {
      toast.error("Viber integrim nuk është i aktivizuar");
      return;
    }

    const formattedPhone = formatPhoneNumber(phoneNumber);
    const message = createMessage(viberConfig.default_message, 'viber');
    
    // Viber URL scheme
    const viberUrl = `viber://chat?number=%2B${formattedPhone}&text=${encodeURIComponent(message)}`;
    
    // Try to open Viber app, fallback to web
    const link = document.createElement('a');
    link.href = viberUrl;
    link.click();
    
    // Fallback: if Viber app is not available, show message
    setTimeout(() => {
      toast.info("Nëse Viber nuk u hap, instaloni aplikacionin ose përdorni Viber Web");
    }, 1000);
  };

  if (loading) {
    return (
      <div className="flex items-center space-x-2">
        <div className="w-4 h-4 animate-spin rounded-full border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="flex items-center space-x-2">
      {/* Phone number display */}
      <span className="text-sm text-slate-600 flex items-center">
        <Phone className="w-3 h-3 mr-1" />
        {phoneNumber}
      </span>
      
      {/* WhatsApp button */}
      {whatsappConfig && whatsappConfig.is_enabled && (
        <Button
          size="sm"
          variant="outline"
          onClick={openWhatsApp}
          className="bg-green-50 border-green-200 hover:bg-green-100 text-green-700 px-2 py-1 h-7"
          title="Dërgo mesazh në WhatsApp"
        >
          <MessageCircle className="w-3 h-3 mr-1" />
          WhatsApp
        </Button>
      )}
      
      {/* Viber button */}
      {viberConfig && viberConfig.is_enabled && (
        <Button
          size="sm"
          variant="outline"
          onClick={openViber}
          className="bg-purple-50 border-purple-200 hover:bg-purple-100 text-purple-700 px-2 py-1 h-7"
          title="Dërgo mesazh në Viber"
        >
          <MessageCircle className="w-3 h-3 mr-1" />
          Viber
        </Button>
      )}
    </div>
  );
};

export default MessagingButtons;